import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Checkbox, ConfigProvider, Input, Select, Table } from 'antd';
import type { TableColumnsType } from 'antd';
import { usePlatformState } from '../../../features/platform/platformStateSlice';
import { useGetGovernmentQuery, useSaveGovernmentMutation } from '../../../features/platform/platformApi';
import { useGetUsersQuery, useDeleteUserMutation, userName, type AppUser } from '../../../features/users/usersApi';
import { EditSquareIcon, TrashIcon } from '../../../assets/icons';
import Breadcrumbs from '../../../components/Breadcrumbs';
import ConfirmDeleteModal from '../../../components/ConfirmDeleteModal';
import { PLATFORM_TABLE_THEME } from './tableTheme';
import { DEPARTMENTS } from './mockData';

/* ------------------------------------------------------------------ *
 * Platform Admin — Government (prototype `isGov`).
 * Self-contained editor ported from the prototype: Executive & portfolios
 * and Legislature. Local React state, no backend. Offices & staff is not
 * part of this screen.
 * ------------------------------------------------------------------ */

/* ---- Data model (ported from the prototype, offices dropped) ------- */

type PosKind =
  | 'governor'
  | 'cm'
  | 'dcm'
  | 'cabinet'
  | 'mosic'
  | 'mos'
  | 'speaker'
  | 'dspeaker'
  | 'lopa'
  | 'chairman'
  | 'dchairman'
  | 'lopc';

const MIN_KINDS: PosKind[] = ['cm', 'dcm', 'cabinet', 'mosic', 'mos'];

const POS_KINDS: Record<PosKind, { title: string; how: string }> = {
  governor: { title: 'Governor', how: 'Appointed by the President (Art. 155); holds office during the President’s pleasure, usually 5 years.' },
  cm: { title: 'Chief Minister', how: 'Appointed by the Governor; normally the leader of the majority in the Assembly.' },
  dcm: { title: 'Deputy Chief Minister', how: 'Not a constitutional post: a Cabinet Minister given the title.' },
  cabinet: { title: 'Cabinet Minister', how: '' },
  mosic: { title: 'Minister of State (Independent Charge)', how: '' },
  mos: { title: 'Minister of State', how: '' },
  speaker: { title: 'Speaker', how: 'Elected by the members of the Assembly.' },
  dspeaker: { title: 'Deputy Speaker', how: 'Elected by the members of the Assembly.' },
  lopa: { title: 'Leader of Opposition', how: 'Leader of the largest opposition party in the Assembly.' },
  chairman: { title: 'Chairman', how: 'Elected by the members of the Council.' },
  dchairman: { title: 'Deputy Chairman', how: 'Elected by the members of the Council.' },
  lopc: { title: 'Leader of Opposition', how: 'Leader of the largest opposition party in the Council.' },
};

const LEG_LADDER = [
  'Principal Secretary / Secretary',
  'Additional Secretary · Joint Secretary',
  'Deputy Secretary',
  'Under Secretary',
  'Section Officer',
  'Assistants · Clerical staff',
];

const BICAMERAL = ['AP', 'BR', 'KA', 'MH', 'TS', 'UP'];

/** Departments enabled for this state (prototype `s.list`). */
const ENABLED_DEPT_CODES = ['01', '02', '03', '11', '12'];

type HouseKey = 'assembly' | 'council';

interface Position {
  id: string;
  kind: PosKind;
  person: string;
  party: string;
  constituency: string;
  depts: string[];
  reportsTo: string;
}

interface CommitteeMember {
  /** Display name of the picked person (stored alongside the directory id). */
  person: string;
  house: HouseKey;
  role: 'Chairperson' | 'Member';
  /** Directory (access/users) id of the picked person. */
  user_id?: number;
}

interface Committee {
  id: string;
  name: string;
  type: 'Financial' | 'House' | 'Subject' | 'Ad hoc';
  servicing: HouseKey;
  depts: string[];
  members: CommitteeMember[];
}

interface Gov {
  hasCouncil: boolean;
  positions: Position[];
  committees: Committee[];
  secretariat: Record<HouseKey, string>;
}

function govPos(id: string, kind: PosKind, extra?: Partial<Position>): Position {
  return { id, kind, person: '', party: '', constituency: '', depts: [], reportsTo: '', ...extra };
}

function councilBits(): Position[] {
  return [govPos('g6', 'chairman'), govPos('g7', 'dchairman'), govPos('g8', 'lopc')];
}

function seedGov(code: string): Gov {
  const council = BICAMERAL.indexOf(code) >= 0;
  return {
    hasCouncil: council,
    positions: [
      govPos('g1', 'governor'),
      govPos('g2', 'cm'),
      govPos('g3', 'speaker'),
      govPos('g4', 'dspeaker'),
      govPos('g5', 'lopa'),
    ].concat(council ? councilBits() : []),
    committees: [
      { id: 'c1', name: 'Public Accounts Committee', type: 'Financial', servicing: 'assembly', depts: [], members: [] },
      { id: 'c2', name: 'Estimates Committee', type: 'Financial', servicing: 'assembly', depts: [], members: [] },
      { id: 'c3', name: 'Committee on Public Undertakings', type: 'Financial', servicing: 'assembly', depts: [], members: [] },
      { id: 'c4', name: 'Business Advisory Committee', type: 'House', servicing: 'assembly', depts: [], members: [] },
    ],
    secretariat: { assembly: '', council: '' },
  };
}

/** Seeded Rajasthan government (prototype `GOV_RJ`). */
const GOV_RJ: Gov = (() => {
  const g = seedGov('RJ');
  const positions = g.positions
    .map((p) => (p.kind === 'cm' ? { ...p, person: '[Chief Minister]', depts: ['02'] } : p))
    .concat([
      govPos('m1', 'cabinet', { person: '[Revenue Minister]', depts: ['01'] }),
      govPos('m2', 'cabinet', { person: '[Health Minister]', depts: ['12'] }),
      govPos('m3', 'cabinet', { person: '[Panchayati Raj Minister]', depts: ['03'] }),
      govPos('m4', 'cabinet', { person: '[Education Minister]', depts: ['11'] }),
      govPos('m5', 'mos', { person: '[MoS, Revenue]', depts: ['01'], reportsTo: 'm1' }),
    ]);
  const committees = g.committees
    .map((c) =>
      c.id === 'c1'
        ? {
            ...c,
            members: [
              { person: '[MLA 1]', house: 'assembly' as const, role: 'Chairperson' as const },
              { person: '[MLA 2]', house: 'assembly' as const, role: 'Member' as const },
              { person: '[MLA 3]', house: 'assembly' as const, role: 'Member' as const },
            ],
          }
        : c,
    )
    .concat([
      {
        id: 'c5',
        name: '[Subject committee] Rural development and Panchayati Raj',
        type: 'Subject',
        servicing: 'assembly',
        depts: ['03'],
        members: [{ person: '[MLA 4]', house: 'assembly', role: 'Chairperson' }],
      },
    ]);
  return { ...g, positions, committees };
})();

/** Resolve a department code → display name. */
function deptName(code: string): string {
  const d = DEPARTMENTS.find((x) => x.code === code);
  return d ? d.name : code;
}

const norm = (s: string) => s.trim().toLowerCase();
const asArr = (v?: string | string[] | null): string[] => (Array.isArray(v) ? v.filter(Boolean) : v ? [v] : []);
// Designation titles that make someone a minister (drives the Council of Ministers list).
const MINISTER_TITLES = MIN_KINDS.map((k) => norm(POS_KINDS[k].title));
const designationsOf = (u: AppUser): string[] => [...asArr(u.designation), ...(u.designations ?? [])];
const ministerTitleOf = (u: AppUser): string =>
  designationsOf(u).find((d) => MINISTER_TITLES.includes(norm(d))) ??
  designationsOf(u).find((d) => norm(d).includes('minister')) ??
  '';
const partyOf = (u: AppUser): string => {
  const prof = u.profile as
    | { politicalParty?: string; party_name?: string; party?: string }
    | null
    | undefined;
  return (
    prof?.politicalParty ||
    (u as { party_name?: string }).party_name ||
    prof?.party_name ||
    prof?.party ||
    ''
  );
};

/* ---- Component ----------------------------------------------------- */

const MINISTER_PLACEHOLDER: Partial<Record<PosKind, string>> = {
  cm: '[Chief Minister]',
  dcm: '[Deputy Chief Minister]',
  cabinet: '[Cabinet Minister]',
  mosic: '[Minister of State (IC)]',
  mos: '[Minister of State]',
};

type TabKey = 'exec' | 'leg';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'exec', label: 'Executive & portfolios' },
  { key: 'leg', label: 'Legislature' },
];

export default function GovernmentPage() {
  const { code, name } = usePlatformState();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Returning from an add form carries ?tab= so the right tab reopens.
  const [tab, setTab] = useState<TabKey>((searchParams.get('tab') as TabKey) || 'exec');
  const seedFor = (c: string): Gov => (c === 'RJ' ? GOV_RJ : seedGov(c));
  const [gov, setGov] = useState<Gov>(() => seedFor(code));
  const [nextId, setNextId] = useState(1);
  const [savedFlash, setSavedFlash] = useState(false);

  // Load/save the whole structure per state (backend stores it as JSON).
  const { data: loadedGov } = useGetGovernmentQuery(code, { skip: !code });
  // Department code→name map from the backend — used to show names (not "01") in the table.
  const deptNameMap = loadedGov?.departments ?? {};
  const [saveGovernment, { isLoading: saving }] = useSaveGovernmentMutation();

  // Explicit save of the whole government object (positions + committees + members).
  const saveNow = () => {
    saveGovernment({ code, data: gov })
      .unwrap()
      .then(() => {
        setSavedFlash(true);
        window.setTimeout(() => setSavedFlash(false), 2000);
      })
      .catch(() => {});
  };
  // Hydrate from the server when it resolves (or fall back to the seed if empty).
  // Edits are persisted only when the user clicks Save (no auto-save).
  useEffect(() => {
    if (!loadedGov) return;
    const data = loadedGov.data ?? {};
    const hasData = Object.keys(data).length > 0;
    setGov(hasData ? (data as unknown as Gov) : seedFor(code));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedGov, code]);

  const NAME = name.toUpperCase();

  // Who actually holds each post — read from the elected people directory, scoped to the state.
  const { data: electedData } = useGetUsersQuery({ directory: 'elected', state: code, all: true });
  const elected = useMemo(() => electedData?.items ?? [], [electedData]);

  // Committee member options from the elected directory: MLAs for Assembly members,
  // MLCs (council members) for Council members. Show the name, store the person's id.
  const { data: mlaData } = useGetUsersQuery({ directory: 'elected', designation: 'MLA', state: code, all: true });
  const { data: mlcData } = useGetUsersQuery({ directory: 'elected', designation: 'MLC', state: code, all: true });
  // Council of Ministers — filtered on the backend (designation contains "Minister"), not in the UI.
  const { data: ministerData } = useGetUsersQuery({ directory: 'elected', designation: 'Minister', state: code, all: true });
  const mlaOptions = useMemo(
    () => (mlaData?.items ?? []).map((u) => ({ value: String(u.id), label: userName(u) })),
    [mlaData],
  );
  const mlcOptions = useMemo(
    () => (mlcData?.items ?? []).map((u) => ({ value: String(u.id), label: userName(u) })),
    [mlcData],
  );
  // Match a position to a directory person: by designation, plus department for ministers.
  const userFor = (p: Position) => {
    const title = norm(POS_KINDS[p.kind].title);
    // CM and Governor hold many/no portfolios → match on designation alone.
    const deptNm = p.kind === 'cm' || p.kind === 'governor' ? '' : p.depts[0] ? norm(deptName(p.depts[0])) : '';
    return (
      elected.find((u) => {
        const desigs = asArr(u.designation).map(norm);
        if (!desigs.includes(title)) return false;
        if (!deptNm) return true;
        // A portfolio is required: the person must actually hold that department. A
        // department-less minister must NOT match every portfolio row (that made one
        // record appear as the holder of all Cabinet Minister rows).
        const depts = asArr(u.department).map(norm);
        return depts.some((d) => d === deptNm || d.includes(deptNm) || deptNm.includes(d));
      }) ?? null
    );
  };
  const personFor = (p: Position): string => {
    const match = userFor(p);
    return match ? userName(match) : p.person;
  };

  /* ---- derived -------------------------------------------------- */
  // The Council of Ministers is the real directory, filtered server-side by designation.
  // Order by rank so the Chief Minister is first (then Deputy CM, Cabinet, MoS…).
  const apiMinisters = useMemo(() => {
    const rank = (u: AppUser) => {
      const i = MINISTER_TITLES.indexOf(norm(ministerTitleOf(u)));
      return i === -1 ? MINISTER_TITLES.length : i;
    };
    return (ministerData?.items ?? []).slice().sort((a, b) => rank(a) - rank(b));
  }, [ministerData]);
  const governor = gov.positions.find((p) => p.kind === 'governor');
  const governorName = governor ? personFor(governor) : '';

  // "Departments without a minister" — enabled departments not held by any minister record.
  const ministerDeptNames = Array.from(
    new Set(apiMinisters.flatMap((u) => asArr(u.department).map(norm))),
  );
  const unassigned = ENABLED_DEPT_CODES.filter((code) => {
    const nm = norm(deptName(code));
    return !ministerDeptNames.some((d) => d === nm || d.includes(nm) || nm.includes(d));
  });

  /* ---- mutations ------------------------------------------------ */
  const patchGov = (patch: Partial<Gov>) => setGov((g) => ({ ...g, ...patch }));
  const removePos = (id: string) =>
    setGov((g) => ({ ...g, positions: g.positions.filter((p) => p.id !== id) }));

  // + Add minister opens the add form (new Cabinet Minister; pick the person + portfolio there).
  const addMinister = () => navigate('/admin/government/add-minister');

  const setHasCouncil = (on: boolean) => {
    setGov((g) => {
      let positions = g.positions;
      if (on && !positions.some((p) => p.kind === 'chairman')) positions = positions.concat(councilBits());
      return { ...g, hasCouncil: on, positions };
    });
  };

  const updCommittee = (id: string, patch: Partial<Committee>) =>
    setGov((g) => ({ ...g, committees: g.committees.map((c) => (c.id === id ? { ...c, ...patch } : c)) }));
  const removeCommittee = (id: string) =>
    setGov((g) => ({ ...g, committees: g.committees.filter((c) => c.id !== id) }));
  const addCommittee = () => {
    const id = 'cx' + nextId;
    setNextId((n) => n + 1);
    setGov((g) => ({
      ...g,
      // New committee goes to the top of the list.
      committees: [{ id, name: 'New committee', type: 'Subject', servicing: 'assembly', depts: [], members: [] }, ...g.committees],
    }));
  };

  // Open the add form under the Government URL (keeps the sidebar active) and return to the
  // originating tab. Designation = base title; the portfolio goes to the Department field.
  const goEdit = (p: Position, backTab: TabKey = 'exec') => {
    const base = POS_KINDS[p.kind].title;
    // If this position already resolves to a directory person, edit THAT record so every saved
    // field (phone, gender, term, …) loads and Save updates it in place. Otherwise, open the
    // create form prefilled with what we know.
    const match = userFor(p);
    const resolved = personFor(p);
    const name = resolved && !/^\[.*\]$/.test(resolved) ? resolved : '';
    const namePart = name ? `&name=${encodeURIComponent(name)}` : '';
    // Pass the department NAME, not the code (deptSel went straight into the form's dept field).
    const deptSel = p.depts[0] ? `&deptSel=${encodeURIComponent(deptName(p.depts[0]))}` : '';
    const back = encodeURIComponent(`/admin/government?tab=${backTab}`);
    const qs = `designation=${encodeURIComponent(base)}&lockDesignation=1&returnTo=${back}${namePart}${deptSel}`;
    navigate(
      match?.id != null
        ? `/admin/government/people/political/${match.id}/edit?${qs}`
        : `/admin/government/people/political/new?${qs}`,
    );
  };

  const [deleteUser, { isLoading: deletingMinister }] = useDeleteUserMutation();
  const [ministerToDelete, setMinisterToDelete] = useState<AppUser | null>(null);
  // Delete a minister's directory record (invalidates Users → the table refetches).
  const confirmDeleteMinister = async () => {
    if (ministerToDelete?.id == null) return;
    try {
      await deleteUser(ministerToDelete.id).unwrap();
      setMinisterToDelete(null);
    } catch {
      /* surfaced by the API error handler elsewhere */
    }
  };

  // Edit a minister straight from their directory record (loads the full record in place).
  const goEditUser = (u: AppUser) => {
    const desig = ministerTitleOf(u);
    const dept = asArr(u.department)[0] ?? '';
    const back = encodeURIComponent('/admin/government?tab=exec');
    const qs = [
      desig ? `designation=${encodeURIComponent(desig)}` : '',
      'lockDesignation=1',
      `returnTo=${back}`,
      dept ? `deptSel=${encodeURIComponent(dept)}` : '',
    ]
      .filter(Boolean)
      .join('&');
    navigate(`/admin/government/people/political/${u.id}/edit?${qs}`);
  };

  /* ---- shared option lists -------------------------------------- */
  const houseOpts: { value: HouseKey; label: string }[] = [
    { value: 'assembly' as HouseKey, label: 'Assembly (MLA)' },
    ...(gov.hasCouncil ? [{ value: 'council' as HouseKey, label: 'Council (MLC)' }] : []),
  ];
  const servOpts: { value: HouseKey; label: string }[] = [
    { value: 'assembly' as HouseKey, label: 'Assembly Secretariat' },
    ...(gov.hasCouncil ? [{ value: 'council' as HouseKey, label: 'Council Secretariat' }] : []),
  ];

  const deptAddOpts = (used: string[], label: string) =>
    [{ value: '', label }].concat(
      ENABLED_DEPT_CODES.filter((k) => used.indexOf(k) < 0).map((k) => ({ value: k, label: deptName(k) })),
    );

  const cardCls = 'rounded-xl border border-line/30 bg-white p-5 shadow-card';
  const labelCls = 'text-xs font-semibold text-ink-secondary';
  const ACTION_BTN =
    'flex items-center justify-center rounded-lg p-2 text-ink-secondary transition-colors hover:bg-brand-shade hover:text-brand';

  const ministerColumns: TableColumnsType<AppUser> = [
    {
      title: 'Position',
      key: 'pos',
      render: (_, u) => <span className="font-semibold text-ink">{ministerTitleOf(u) || '—'}</span>,
    },
    {
      title: 'Name',
      key: 'name',
      render: (_, u) => <span className="text-ink">{userName(u)}</span>,
    },
    { title: 'Party', key: 'party', width: 150, render: (_, u) => <span className="text-ink">{partyOf(u) || '—'}</span> },
    {
      title: 'Constituency',
      key: 'con',
      width: 170,
      render: (_, u) => <span className="text-ink">{u.location_name || '—'}</span>,
    },
    {
      title: 'Departments',
      key: 'depts',
      render: (_, u) => {
        // `department` may be a single code, a list, or a comma-joined string
        // ("02, 03") — split it out and show the name, not the number.
        const depts = asArr(u.department)
          .flatMap((d) => d.split(','))
          .map((d) => d.trim())
          .filter(Boolean);
        return depts.length ? (
          <span className="flex flex-wrap gap-1">
            {depts.map((d) => (
              <span key={d} className="rounded-md border border-[#dbe6fb] bg-brand-shade px-2 py-0.5 text-xs font-medium text-brand-dark">
                {deptNameMap[d] ?? d}
              </span>
            ))}
          </span>
        ) : (
          <span className="text-ink-secondary/60">—</span>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      width: 100,
      align: 'right',
      render: (_, u) => (
        <div className="flex items-center justify-end gap-1">
          <button type="button" aria-label="Edit" title="Edit" onClick={() => goEditUser(u)} className={ACTION_BTN}>
            <EditSquareIcon className="size-4" />
          </button>
          <button
            type="button"
            aria-label="Delete"
            title="Delete"
            onClick={() => setMinisterToDelete(u)}
            className={[ACTION_BTN, 'hover:bg-[#fef2f2] hover:text-[#dc2626]'].join(' ')}
          >
            <TrashIcon className="size-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-[18px]">
      <Breadcrumbs items={[{ label: 'All states', to: '/admin/overview' }, { label: 'Government' }]} />
      <section className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-semibold text-brand">
            PLATFORM ADMIN · {NAME} · SHARED BY ALL {NAME} CLIENTS
          </span>
          {saving && <span className="text-xs font-medium text-ink-secondary">Saving…</span>}
        </div>
        <h1 className="text-2xl font-semibold leading-9 tracking-[-0.75px] text-ink">Government</h1>
        <p className="max-w-[820px] text-[13px] text-ink-secondary">
          Who holds political office in {name}, the departments each minister is responsible for, and the
          legislature. A minister’s departments extend escalation: after the senior-most officer, a request goes
          to the minister in charge.
        </p>
        <div className="flex flex-wrap gap-2 pt-1.5">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={[
                'h-10 rounded-lg px-3.5 text-[13px] font-semibold transition-colors',
                tab === t.key
                  ? 'border border-brand bg-brand-shade text-brand'
                  : 'border border-line bg-white text-ink-secondary hover:bg-brand-shade',
              ].join(' ')}
            >
              {t.label}
            </button>
          ))}
        </div>
      </section>

      {tab === 'exec' && (
        <div className="flex flex-col gap-[18px]">
          {/* Head of State / Governor */}
          {governor && (
            <section className={`${cardCls} flex flex-wrap items-end gap-x-6 gap-y-3`}>
              <div className="flex flex-[1_1_320px] flex-col gap-1">
                <span className="text-[11px] font-bold uppercase tracking-[0.55px] text-ink-secondary">
                  Head of State
                </span>
                <span className="text-base font-bold text-ink">Governor</span>
                <span className="text-xs text-ink-secondary">{POS_KINDS.governor.how}</span>
              </div>
              <div className="flex flex-[1_1_260px] flex-col gap-1">
                <span className={labelCls}>Holder</span>
                <span className={['flex min-h-[44px] items-center text-sm', governorName ? 'text-ink' : 'text-ink-secondary/70'].join(' ')}>
                  {governorName || 'Not recorded'}
                </span>
              </div>
              <button
                type="button"
                onClick={() => goEdit(governor)}
                className="h-10 shrink-0 rounded-lg bg-brand px-4 text-[13px] font-semibold text-white shadow-btn transition-colors hover:bg-brand-dark"
              >
                {governorName ? 'Edit' : 'Add'}
              </button>
            </section>
          )}

          {/* Council of Ministers and portfolios */}
          <section className="overflow-hidden rounded-xl border border-line/30 bg-white shadow-card">
            <div className="flex flex-wrap items-center justify-between gap-2.5 p-5">
              <div className="flex flex-col gap-0.5">
                <h2 className="text-base font-bold text-ink">Council of Ministers and portfolios</h2>
                <span className="text-xs text-ink-secondary">
                  {apiMinisters.length} minister{apiMinisters.length === 1 ? '' : 's'} on record. The Council of
                  Ministers can’t exceed 15% of the Assembly’s strength, and must have at least 12 (Art. 164).
                </span>
              </div>
              <button
                type="button"
                onClick={addMinister}
                className="h-10 shrink-0 rounded-lg bg-brand px-4 text-[13px] font-semibold text-white shadow-btn transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-50"
              >
                + Add minister
              </button>
            </div>

            <ConfigProvider theme={PLATFORM_TABLE_THEME}>
              <Table<AppUser>
                className="chunaav-table"
                columns={ministerColumns}
                dataSource={apiMinisters}
                rowKey="id"
                locale={{ emptyText: 'No ministers on record for this state yet.' }}
                pagination={false}
                scroll={{ x: 'max-content' }}
              />
            </ConfigProvider>
          </section>

          {/* Departments without a minister */}
          {unassigned.length > 0 && (
            <section className="flex flex-wrap items-center gap-2 rounded-xl border border-[#f1e3bd] bg-[#fffbeb] px-4 py-3.5">
              <span className="text-[13px] font-semibold text-[#8a4b0f]">
                Departments without a minister — escalation stops at the senior-most officer:
              </span>
              {unassigned.map((k) => (
                <span
                  key={k}
                  className="rounded-full border border-[#e8d5a6] bg-white px-2.5 py-0.5 text-xs font-medium text-[#8a4b0f]"
                >
                  {deptName(k)}
                </span>
              ))}
            </section>
          )}
        </div>
      )}

      {tab === 'leg' && (
        <div className="flex flex-col gap-3.5">
          <section className={`${cardCls} flex flex-wrap items-center justify-between gap-3`}>
            <p className="text-[13px] text-ink-secondary">
              {gov.hasCouncil
                ? `${name} has two Houses: the Legislative Assembly and the Legislative Council.`
                : `${name} has one House: the Legislative Assembly.`}
            </p>
            <label className="flex items-center gap-2 text-[13px] font-semibold text-ink">
              <Checkbox checked={gov.hasCouncil} onChange={(e) => setHasCouncil(e.target.checked)} />
              Legislative Council (bicameral)
            </label>
          </section>

          {/* Houses */}
          <div className="grid grid-cols-[repeat(auto-fit,minmax(340px,1fr))] gap-3.5">
            {(
              [
                {
                  key: 'assembly' as HouseKey,
                  houseName: 'Legislative Assembly · Vidhan Sabha',
                  note: 'MLAs, directly elected from constituencies. 5-year term unless dissolved earlier.',
                  kinds: ['speaker', 'dspeaker', 'lopa'] as PosKind[],
                },
              ].concat(
                gov.hasCouncil
                  ? [
                      {
                        key: 'council' as HouseKey,
                        houseName: 'Legislative Council · Vidhan Parishad',
                        note: 'Permanent House. MLCs serve 6 years; a third retire every 2 years. Chosen by MLAs, local bodies, graduates and teachers, and nominated by the Governor.',
                        kinds: ['chairman', 'dchairman', 'lopc'] as PosKind[],
                      },
                    ]
                  : [],
              )
            ).map((h) => {
              const officers = h.kinds
                .map((k) => gov.positions.find((x) => x.kind === k))
                .filter((p): p is Position => Boolean(p));
              return (
                <section key={h.key} className={`${cardCls} flex flex-col gap-2.5`}>
                  <h2 className="text-base font-bold text-ink">{h.houseName}</h2>
                  <span className="text-xs text-ink-secondary">{h.note}</span>
                  <span className="mt-1 text-[11px] font-bold uppercase tracking-[0.55px] text-ink-secondary">
                    Presiding officers
                  </span>
                  {officers.map((p) => (
                    <div key={p.id} className="flex flex-col gap-1">
                      <span className={labelCls}>{POS_KINDS[p.kind].title}</span>
                      <div className="flex items-center gap-2">
                        <span
                          className={[
                            'flex min-h-[44px] flex-1 items-center rounded-lg border border-line bg-muted-bg px-3 text-sm',
                            p.person ? 'text-ink' : 'text-ink-secondary/70',
                          ].join(' ')}
                        >
                          {p.person || 'Not recorded'}
                        </span>
                        <button
                          type="button"
                          onClick={() => goEdit(p, 'leg')}
                          className="h-11 shrink-0 rounded-lg border border-brand bg-white px-3.5 text-xs font-semibold text-brand transition-colors hover:bg-brand-shade"
                        >
                          {p.person ? 'Edit' : 'Add'}
                        </button>
                      </div>
                      <span className="text-[11px] text-ink-secondary/60">{POS_KINDS[p.kind].how}</span>
                    </div>
                  ))}
                  <span className="mt-1 text-[11px] font-bold uppercase tracking-[0.55px] text-ink-secondary">
                    Secretariat · permanent staff, independent of the government
                  </span>
                  <div className="flex flex-col gap-1">
                    <span className={labelCls}>Head (Principal Secretary or Secretary)</span>
                    <div className="flex items-center gap-2">
                      <Input
                        size="large"
                        className="flex-1"
                        value={gov.secretariat[h.key]}
                        placeholder="Not recorded"
                        onChange={(e) =>
                          patchGov({ secretariat: { ...gov.secretariat, [h.key]: e.target.value } })
                        }
                      />
                      <button
                        type="button"
                        onClick={() =>
                          navigate(
                            `/admin/government/people/government-official/new?designation=${encodeURIComponent('Principal Secretary / Secretary')}&lockDesignation=1&returnTo=${encodeURIComponent('/admin/government?tab=leg')}`,
                          )
                        }
                        className="h-11 shrink-0 rounded-lg border border-brand bg-white px-3.5 text-xs font-semibold text-brand transition-colors hover:bg-brand-shade"
                      >
                        {gov.secretariat[h.key] ? 'Edit' : 'Add'}
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-1">
                    {LEG_LADDER.map((l) => (
                      <span key={l} className="rounded-md border border-line px-2 py-0.5 text-[11px] text-ink-secondary">
                        {l}
                      </span>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>

          {/* Committees */}
          <section className="overflow-hidden rounded-xl border border-line/30 bg-white shadow-card">
            <div className="flex flex-wrap items-center justify-between gap-2.5 p-5">
              <div className="flex flex-col gap-0.5">
                <h2 className="text-base font-bold text-ink">Committees</h2>
                <span className="text-xs text-ink-secondary">
                  Members can come from both Houses. Each member’s own House is recorded on the membership; the
                  servicing Secretariat only does the paperwork.
                </span>
              </div>
              <div className="flex shrink-0 items-center gap-2.5">
                {savedFlash && <span className="text-[13px] font-semibold text-brand">Saved</span>}
                <button
                  type="button"
                  onClick={addCommittee}
                  className="h-10 rounded-lg border border-brand bg-white px-4 text-[13px] font-semibold text-brand transition-colors hover:bg-brand-shade"
                >
                  + Add committee
                </button>
                <button
                  type="button"
                  onClick={saveNow}
                  disabled={saving}
                  className="h-10 rounded-lg bg-brand px-4 text-[13px] font-semibold text-white shadow-btn transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-3 px-5 pb-5">
              {gov.committees.length === 0 && (
                <span className="text-[13px] text-ink-secondary">No committees yet.</span>
              )}
              {gov.committees.map((c) => {
                const mla = c.members.filter((m) => m.house === 'assembly').length;
                const mlc = c.members.filter((m) => m.house === 'council').length;
                const compTxt =
                  `${c.members.length} member${c.members.length === 1 ? '' : 's'}` +
                  (gov.hasCouncil ? ` · ${mla} MLA, ${mlc} MLC` : '');
                const updMember = (i: number, patch: Partial<CommitteeMember>) =>
                  updCommittee(c.id, { members: c.members.map((m, j) => (j === i ? { ...m, ...patch } : m)) });
                return (
                  <div key={c.id} className="flex flex-col gap-3 rounded-lg border border-line/50 bg-white p-4">
                    {/* Header: name / type / serviced-by + remove */}
                    <div className="flex flex-wrap items-end gap-2.5">
                      <label className="flex min-w-[200px] flex-1 flex-col gap-1">
                        <span className={labelCls}>Committee</span>
                        <Input size="large" value={c.name} onChange={(e) => updCommittee(c.id, { name: e.target.value })} />
                      </label>
                      <label className="form-field-select flex w-[150px] flex-col gap-1">
                        <span className={labelCls}>Type</span>
                        <Select
                          size="large"
                          value={c.type}
                          onChange={(v) => updCommittee(c.id, { type: v as Committee['type'] })}
                          options={(['Financial', 'House', 'Subject', 'Ad hoc'] as const).map((o) => ({ value: o, label: o }))}
                        />
                      </label>
                      <label className="form-field-select flex w-[190px] flex-col gap-1">
                        <span className={labelCls}>Serviced by</span>
                        <Select
                          size="large"
                          value={c.servicing}
                          onChange={(v) => updCommittee(c.id, { servicing: v as HouseKey })}
                          options={servOpts}
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => removeCommittee(c.id)}
                        aria-label="Remove committee"
                        title="Remove committee"
                        className="flex h-11 shrink-0 items-center justify-center rounded-lg border border-[#fecaca] bg-white px-3 text-ink-secondary transition-colors hover:bg-[#fef2f2] hover:text-[#dc2626]"
                      >
                        <TrashIcon className="size-4" />
                      </button>
                    </div>

                    {/* Examines */}
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={labelCls}>Examines:</span>
                      {c.depts.map((k) => (
                        <span
                          key={k}
                          className="inline-flex items-center gap-0.5 rounded-md border border-[#dbe6fb] bg-brand-shade py-0.5 pl-2.5 pr-1 text-xs font-medium text-brand-dark"
                        >
                          {deptName(k)}
                          <button
                            type="button"
                            aria-label={`Remove ${deptName(k)}`}
                            onClick={() => updCommittee(c.id, { depts: c.depts.filter((x) => x !== k) })}
                            className="flex size-5 items-center justify-center rounded text-[13px] leading-none text-ink-secondary transition-colors hover:bg-brand/10 hover:text-brand"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                      {c.depts.length === 0 && <span className="text-xs text-ink-secondary/60">No departments</span>}
                      <div className="form-field-select">
                        <Select
                          aria-label="Add a department examined"
                          value=""
                          style={{ minWidth: 200 }}
                          onChange={(v) => {
                            if (v) updCommittee(c.id, { depts: c.depts.concat([v]) });
                          }}
                          options={deptAddOpts(c.depts, 'Examines a department…')}
                        />
                      </div>
                      <span className="flex-1" />
                      <span className="rounded-full bg-muted-bg px-2.5 py-0.5 text-xs font-medium text-ink-secondary">{compTxt}</span>
                    </div>

                    {/* Members */}
                    <div className="flex flex-col gap-1.5 border-t border-[#f3f4f6] pt-3">
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-bold uppercase tracking-[0.05em] text-ink-secondary">Members</span>
                        <button
                          type="button"
                          onClick={() =>
                            updCommittee(c.id, {
                              // New member goes to the top.
                              members: [{ person: '', house: 'assembly', role: 'Member' }, ...c.members],
                            })
                          }
                          className="h-8 shrink-0 rounded-lg border border-brand bg-white px-3 text-xs font-semibold text-brand transition-colors hover:bg-brand-shade"
                        >
                          + Add member
                        </button>
                      </div>
                      {c.members.length === 0 && (
                        <span className="text-xs text-ink-secondary/60">No members recorded.</span>
                      )}
                      {c.members.map((m, i) => (
                        <div
                          key={i}
                          className="grid grid-cols-[minmax(180px,2fr)_minmax(150px,1fr)_minmax(130px,1fr)_40px] items-center gap-2"
                        >
                          <div className="form-field-select">
                            <Select
                              aria-label="Member name"
                              className="w-full"
                              placeholder="Select member"
                              showSearch
                              optionFilterProp="label"
                              // Keyed on the directory id; shows the name. Falls back to the stored
                              // name for legacy members saved before ids were recorded.
                              value={m.user_id != null ? String(m.user_id) : m.person || undefined}
                              onChange={(v) => {
                                const opts = m.house === 'council' ? mlcOptions : mlaOptions;
                                const picked = opts.find((o) => o.value === v);
                                // Store the name in `person` and the directory id in `user_id`.
                                updMember(i, {
                                  person: picked?.label ?? '',
                                  user_id: v ? Number(v) : undefined,
                                });
                              }}
                              // Assembly members pick from MLAs; Council members from MLCs.
                              // Disable people already picked by another member of this committee —
                              // the same person can't be added twice.
                              options={(m.house === 'council' ? mlcOptions : mlaOptions).map((o) => ({
                                ...o,
                                disabled: c.members.some(
                                  (mm, j) =>
                                    j !== i && mm.user_id != null && String(mm.user_id) === o.value,
                                ),
                              }))}
                              allowClear
                            />
                          </div>
                          <div className="form-field-select">
                            <Select
                              aria-label="Member’s House"
                              className="w-full"
                              value={m.house}
                              onChange={(v) => updMember(i, { house: v as HouseKey })}
                              options={houseOpts}
                            />
                          </div>
                          <div className="form-field-select">
                            <Select
                              aria-label="Role"
                              className="w-full"
                              value={m.role}
                              onChange={(v) => updMember(i, { role: v as CommitteeMember['role'] })}
                              options={(['Chairperson', 'Member'] as const).map((o) => ({ value: o, label: o }))}
                            />
                          </div>
                          <button
                            type="button"
                            aria-label="Remove member"
                            title="Remove member"
                            onClick={() => updCommittee(c.id, { members: c.members.filter((_, j) => j !== i) })}
                            className={[ACTION_BTN, 'hover:bg-[#fef2f2] hover:text-[#dc2626]'].join(' ')}
                          >
                            <TrashIcon className="size-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      )}

      <ConfirmDeleteModal
        open={ministerToDelete !== null}
        title="Delete minister"
        message={
          <>
            Are you sure you want to delete{' '}
            <span className="font-semibold text-ink">
              {ministerToDelete ? userName(ministerToDelete) : ''}
            </span>{' '}
            from the directory? This action is permanent and cannot be undone.
          </>
        }
        loading={deletingMinister}
        onClose={() => setMinisterToDelete(null)}
        onConfirm={() => void confirmDeleteMinister()}
      />
    </div>
  );
}
